
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import Layout from '../../../components/docs/Layout';
export const metadata = {
  toc: true,
  layout: 'guide',
  section: 'Transfer',
  title: 'Using Signal with Transfer',
  description: 'Learn how to configure Signal for Transfer'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const Image = makeShortcode("Image");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <Header title={metadata.title} subtitle={metadata.description} mdxType="Header" />
    <p>{`Before they can be initiated, transfers must be authorized by Plaid's Payment risk platform, Signal. Signal's risk engine is executed when `}<inlineCode parentName="p">{`/transfer/authorization/create`}</inlineCode>{` is called to determine whether the transfer should proceed or be rerouted to a different payment method.`}</p>
    <Callout warning mdxType="Callout">
      <p>{`If you are adding Signal to an existing Transfer integration, do not call `}<inlineCode parentName="p">{`/signal/evaluate`}</inlineCode>{` or `}<inlineCode parentName="p">{`/signal/prepare`}</inlineCode>{` directly. Signal is invoked automatically when `}<inlineCode parentName="p">{`/transfer/authorization/create`}</inlineCode>{` is called with a scored `}<inlineCode parentName="p">{`ruleset_key`}</inlineCode>{` (see `}<a parentName="p" {...{
          "href": "#using-rulesets-during-transfer-authorization"
        }}>{`Using rulesets during transfer authorization`}</a>{` below). `}<inlineCode parentName="p">{`/signal/prepare`}</inlineCode>{` applies only to the standalone integration described in `}<a parentName="p" {...{
          "href": "/docs/signal/creating-signal-items/#adding-signal-to-existing-items"
        }}>{`Adding Signal to existing Items`}</a>{`, not to Transfer. If your use case requires calling `}<inlineCode parentName="p">{`/signal/evaluate`}</inlineCode>{` independently of `}<inlineCode parentName="p">{`/transfer/authorization/create`}</inlineCode>{`, contact your Plaid account manager.`}</p>
    </Callout>
    <p>{`Signal uses Signal Rules, which enable you to define your decision logic. These rules are configured directly within the Signal Dashboard, utilizing Plaid-provided templates that you can customize for your specific needs.`}</p>
    <p>{`It is important to note that these configurable Signal Rules apply exclusively to debit transfers and are not used for customers leveraging Transfer solely for payouts (credit transfers). Both debit and credit transfers are subject to an additional, `}<a parentName="p" {...{
        "href": "#default-authorization-decisions"
      }}>{`mandatory set`}</a>{` of risk and compliance checks that occur in `}<inlineCode parentName="p">{`/transfer/authorization/create`}</inlineCode>{`.`}</p>
    <p>{`You can manage your rules in the Signal Rules section of the Dashboard. `}</p>
    <Image src='/assets/img/docs/transfer/signal-rules-default.png' alt="Signal Rules UI showing a set of default rules." caption='The Signal Rules configuration page, with a set of default rules loaded' width='1050' expandable={true} mdxType="Image" />
    <p>{`Each rule produces a result:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`ACCEPT`}</inlineCode>{` and proceed with the transaction. The `}<inlineCode parentName="li">{`decision`}</inlineCode>{` from `}<inlineCode parentName="li">{`/transfer/authorization/create`}</inlineCode>{` will be `}<inlineCode parentName="li">{`approved`}</inlineCode>{`.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`REROUTE`}</inlineCode>{` the customer to a different payment method. The `}<inlineCode parentName="li">{`decision`}</inlineCode>{` from `}<inlineCode parentName="li">{`/transfer/authorization/create`}</inlineCode>{` will be `}<inlineCode parentName="li">{`declined`}</inlineCode>{`.`}</li>
    </ul>
    <p>{`Rules are evaluated sequentially, from top to bottom, typically ordered by the severity of the rule violation. If data that the rule depends on is not available, the rule will be skipped. Every ruleset must include a fallback rule that always matches all remaining transactions.`}</p>
    <p>{`You can customize many of these rules to meet your business's needs.`}</p>
    <h4 {...{
      "id": "default-ruleset-for-real-time-balance-checks"
    }}>{`Default ruleset for real-time balance checks`}</h4>
    <p>{`Signal performs a real-time balance check of the end-user account when evaluating debit transfers. Plaid provisions a default ruleset for balance checks containing four rules that cover the most common balance-related scenarios.  `}</p>
    <Layout.FullWidth>
  <table>
    <thead>
      <tr>
        <th>Rule order</th>
        <th>Condition</th>
        <th style={{
              minWidth: "75px"
            }}>Result</th>
        <th style={{
              whiteSpace: "nowrap",
              minWidth: "200px"
            }}>Decision Rationale Code</th>
        <th>Explanation</th>
        <th>Configurability</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td>1</td>
        <td>
          Is Item Login Required <strong>equals</strong> <code>TRUE</code>
        </td>
        <td><code>ACCEPT</code></td>
        <td style={{
              whiteSpace: "nowrap",
              minWidth: "200px"
            }}><code>ITEM_LOGIN_REQUIRED</code></td>
        <td>
          Item connection is{" "}
          <a href="/docs/errors/item/#item_login_required">broken</a> but can be
          restored using <a href="/docs/link/update-mode/">update mode</a>. A transfer can still be
          processed without restoring the Item, but Plaid cannot retrieve balance information until the Item is restored. See{" "}
          <a href="/docs/transfer/creating-transfers/#repairing-items-in-item_login_required-state">
            Repairing Items in <code>ITEM_LOGIN_REQUIRED</code> state
          </a>.
        </td>
        <td>
          Result can be edited.
        </td>
      </tr>
      <tr>
        <td>2</td>
        <td>
          Account Verification Status <strong>in</strong>{" "}
          [<code>database_insights_pass_with_caution</code>]
        </td>
        <td><code>ACCEPT</code></td>
        <td style={{
              whiteSpace: "nowrap",
              minWidth: "200px"
            }}><code>MANUALLY_VERIFIED_ITEM</code></td>
        <td>
          Item was manually verified via{" "}
          <a href="/docs/auth/coverage/database-auth/#overview">Database Auth</a> and result was <a href="/docs/auth/coverage/database-auth/#verification-status">"pass with caution"</a>. Balance can never be checked on this Item. 
        </td>
        <td>
          Result can be edited.
        </td>
      </tr>
      <tr>
        <td>3</td>
        <td>
          Balance Fetch Succeeded <strong>equals</strong> <code>false</code>
        </td>
        <td><code>ACCEPT</code></td>
        <td style={{
              whiteSpace: "nowrap",
              minWidth: "200px"
            }}><code>ERROR</code></td>
        <td>
          Plaid was unable to fetch the balance of the account for reasons not covered in the previous two rules. You can retry this authorization request later to re-attempt fetching the balance.
        </td>
        <td>
          Result can be edited.
        </td>
      </tr>
      <tr>
        <td>4</td>
        <td>
          Available or Current Balance <strong>≤</strong>{" "}
          <code>Transaction Amount</code>
        </td>
        <td><code>REROUTE</code></td>
        <td style={{
              whiteSpace: "nowrap",
              minWidth: "200px"
            }}><code>NSF</code></td>
        <td>
          Account balance is likely to be insufficient for the transaction amount.
        </td>
        <td>
          Result can be edited.
          <br />
          Rule can be deleted.
        </td>
      </tr>
      <tr>
        <td>5</td>
        <td><em>(Fallback)</em> Remaining transactions</td>
        <td><code>ACCEPT</code></td>
        <td style={{
              whiteSpace: "nowrap",
              minWidth: "200px"
            }}><code>null</code></td>
        <td>
          Transaction did not meet the condition for any other rule above.
        </td>
        <td>
          Fallback rule. Cannot be edited or deleted.
        </td>
      </tr>
    </tbody>
  </table>
    </Layout.FullWidth>
    <p>{`The default ruleset is designed to let you proceed with processing transfers by default. When Plaid is unable to fetch the account balance, the default ruleset produces an `}<inlineCode parentName="p">{`ACCEPT`}</inlineCode>{` result. `}</p>
    <p>{`You can edit the rule outcomes to be more conservative based on your risk tolerance and ongoing return rates. For example, in the case of high NSF returns, you may want to `}<inlineCode parentName="p">{`REROUTE`}</inlineCode>{` transactions when balance fetches fail, when the Item connection is broken, or when the result of Database Auth is `}<inlineCode parentName="p">{`database_insights_pass_with_caution`}</inlineCode>{`. `}</p>
    <h4 {...{
      "id": "creating-additional-custom-rulesets"
    }}>{`Creating additional custom rulesets`}</h4>
    <p>{`If you have your own set of rules you would like to use for balance checks, you can create a custom ruleset via the Plaid Dashboard. You can create multiple rulesets and have different rulesets applied in different situations.`}</p>
    <p>{`For example, you could create a ruleset with stricter rules for first time users (such as not proceeding with the Transfer when the balance could not be fetched) and a separate ruleset with more lenient rules for returning users, allowing you to better balance your risk and user experience.`}</p>
    <p>{`Note: You cannot override Plaid's mandatory risk and compliance checks; these result in a `}<inlineCode parentName="p">{`RISK`}</inlineCode>{` rationale code.`}</p>
    <p>{`To create a ruleset:`}</p>
    <ol>
      <li parentName="ol">{`In the Plaid Dashboard, navigate to `}<a parentName="li" {...{
          "href": "https://dashboard.plaid.com/signal/risk-profiles/"
        }}><strong parentName="a">{`Signal → Rules`}</strong></a></li>
      <li parentName="ol">{`Click `}<strong parentName="li">{`Create`}</strong>{` and follow the guided process. You will be prompted to enter a unique name and key for your ruleset. This key is what you will use to reference the ruleset in your code.`}</li>
      <li parentName="ol">{`(Optional) For most new customers, we recommend starting with the default set of rules. However, if you prefer to customize your ruleset, create rules based on attributes such as Available or Current Balance or the Signal Transaction Score, and drag them into the specific order you want them to be evaluated. `}</li>
      <li parentName="ol">{`Click `}<strong parentName="li">{`Save`}</strong>{` to activate your new ruleset.`}</li>
    </ol>
    <Image src='/assets/img/docs/transfer/new-rule-dialog.png' alt="Transfer UI showing the user creating a new rule to reject transfers where the Item is in a login required state and the transfer value is greater than $75." caption='Creating a new rule with the Create Rule dialog.' width='1200' expandable={true} mdxType="Image" />
    <p>{`As you create and modify rulesets in the Plaid Dashboard, the Dashboard will display back-tested performance for these new rulesets (counts of historical matching transactions and return-rate estimates) so you can gauge the impact of these changes before going live. You can view the performance for any specific rule by clicking on the "..." icon next to the rule and selecting  `}<strong parentName="p">{`View Performance`}</strong>{`. `}</p>
    <h5 {...{
      "id": "disabling-balance-checks"
    }}>{`Disabling balance checks`}</h5>
    <p>{`You can also disable balance checks altogether for a ruleset. For example, you may not want to run balance checks for low-risk, highly trusted customers who are making small transactions.`}</p>
    <p>{`To disable balance checks, go to the rule editor page for the ruleset and click the "Turn off balance checks" button.`}</p>
    <p>{`When a ruleset has balance checks running, a "Running Balance Checks" banner will be displayed next to the ruleset name on both the Ruleset overview page and the Ruleset detail page. When a ruleset does not have balance checks running, the banner will read "Balance Checks Turned Off".`}</p>
    <h4 {...{
      "id": "using-rulesets-during-transfer-authorization"
    }}>{`Using rulesets during transfer authorization`}</h4>
    <p>{`To evaluate a transfer against a custom ruleset, pass the `}<inlineCode parentName="p">{`ruleset_key`}</inlineCode>{` in your `}<inlineCode parentName="p">{`/transfer/authorization/create`}</inlineCode>{` call. If you do not pass in a `}<inlineCode parentName="p">{`ruleset_key`}</inlineCode>{`, Signal will use the `}<inlineCode parentName="p">{`default`}</inlineCode>{` ruleset.`}</p>
    <p>{`Before enabling a new ruleset's decisions in production, you can validate its rules against your real-world outcomes by `}<a parentName="p" {...{
        "href": "/docs/signal/signal-rules/#shadow-tests-and-proof-of-concept-testing"
      }}>{`running it as a shadow test`}</a>{`: configure the ruleset with an `}<inlineCode parentName="p">{`ACCEPT`}</inlineCode>{`-only fallback so it never affects the authorization decision, while still `}<a parentName="p" {...{
        "href": "/docs/signal/reporting-returns"
      }}>{`reporting returns`}</a>{` to Plaid.`}</p>
    <p>{`Plaid will assess the transfer against the corresponding ruleset, and the `}<inlineCode parentName="p">{`authorization`}</inlineCode>{` object in the API response will indicate if the authorization is approved or declined, along with a decision rationale code. Transfers that result in an `}<inlineCode parentName="p">{`ACCEPT`}</inlineCode>{` value will have an `}<inlineCode parentName="p">{`approved`}</inlineCode>{` authorization decision, and transfers that result in `}<inlineCode parentName="p">{`REROUTE`}</inlineCode>{` will have a `}<inlineCode parentName="p">{`declined`}</inlineCode>{` decision.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "switcher=false #Sample authorization object snippet",
        "switcher": "false",
        "#Sample": true,
        "authorization": true,
        "object": true,
        "snippet": true
      }}>{`"authorization": {
  "id": "460cbe92-2dcc-8eae-5ad6-b37d0ec90fd9",
  "decision": "approved",
  "decision_rationale": {
    "code": "ITEM_LOGIN_REQUIRED",
    "description": "Unable to pull required information on account needed for authorization decision due to item staleness."
  }
...
}
`}</code></pre>
    <p>{`When any of the non-fallback rules in a ruleset are triggered, the `}<inlineCode parentName="p">{`decision_rationale.code`}</inlineCode>{` in the `}<inlineCode parentName="p">{`/transfer/authorization/create`}</inlineCode>{` response will indicate which rule condition was met. See the `}<a parentName="p" {...{
        "href": "#default-ruleset-for-real-time-balance-checks"
      }}>{`table`}</a>{` above to map a `}<inlineCode parentName="p">{`decision_rationale.code`}</inlineCode>{` to a default rule. When the decision rationale is `}<inlineCode parentName="p">{`null`}</inlineCode>{`, this means that the transfer passed through to the fallback rule without triggering any of the other rules.`}</p>
    <h4 {...{
      "id": "default-authorization-decisions"
    }}>{`Default authorization decisions`}</h4>
    <h5 {...{
      "id": "all-transactions"
    }}>{`All transactions`}</h5>
    <p>{`The following authorization decisions are applied to all applicable transfers sent to Plaid for authorization. This includes a mandatory set of risk and compliance checks that cannot be disabled or modified.`}</p>
    <Layout.FullWidth>
      <table>
  <thead>
    <tr>
      <th>Transfer Type</th>
      <th>Scenario</th>
      <th>Authorization Decision</th>
      <th style={{
              minWidth: "210px"
            }}>Decision Rationale Code</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td>Debit, Credit</td>
      <td>Items successfully created through manual verification (micro-deposits or database
        authentication) are approved by default for processing because there is no active data connection
        to the Item.</td>
      <td><code>approved</code>*</td>
      <td><code>"MANUALLY_VERIFIED_ITEM"</code></td>
    </tr>
    <tr>
      <td>Debit, Credit</td>
      <td>Items created through <a href="/docs/api/products/transfer/account-linking/#transfermigrate_account"><code>/transfer/migrate_account</code></a>{" "}
         are always approved for processing because there is no active data connection to the Item. See <a href="/docs/transfer/creating-transfers/#importing-account-and-routing-numbers">Importing Account and Routing
          numbers</a>.</td>
      <td><code>approved</code></td>
      <td><code>"MIGRATED_ACCOUNT_ITEM"</code></td>
    </tr>
    <tr>
      <td>Debit, Credit</td>
      <td>Transfer exceeds a limit, such as a monthly or daily transaction limit (see{" "}
        <a href="/docs/transfer/creating-transfers/#transfer_limit_reached"><code>TRANSFER_LIMIT_REACHED</code></a>).</td>
      <td><code>declined</code></td>
      <td><code>"TRANSFER_LIMIT_REACHED"</code></td>
    </tr>
        <tr>
      <td>Debit, Credit</td>
      <td>The Item's <code><a href="/docs/api/products/auth/#auth-get-response-accounts-verification-status">verification_status</a></code> is <code>database_insights_fail</code>, <code>pending_automatic_verification</code>, <code>pending_manual_verification</code>, <code>unsent</code>, <code>verification_expired</code>, or <code>verification_failed</code>.</td>
      <td><code>declined</code></td>
      <td><code>"RISK"</code></td>
    </tr>
    <tr>
      <td>Debit, Credit</td>
      <td>The user's device activity indicates fraud</td>
      <td><code>declined</code></td>
      <td><code>"RISK"</code></td>
    </tr>
        <tr>
      <td>Debit, Credit</td>
      <td>The user's IP address is in an OFAC-sanctioned country</td>
      <td><code>declined</code></td>
      <td><code>"RISK"</code></td>
    </tr>
    <tr>
      <td>Debit, Credit</td>
      <td>There are excessively high rates of insufficient funds, administrative, and/or unauthorized returns associated with the account across the Plaid network.</td>
      <td><code>declined</code></td>
      <td><code>"RISK"</code></td>
    </tr>
    <tr>
      <td>Credit</td>
      <td>Insufficient available Ledger balance for a credit</td>
      <td><code>declined</code></td>
      <td><code>"NSF"</code></td>
    </tr>
  </tbody>
      </table>
      <div style={{
        fontSize: "14px",
        marginBottom: "24px"
      }}> 
        <span> * Default value; can be <a href="#creating-additional-custom-rulesets"><span style={{
              textDecoration: "underline"
            }}>customized</span></a>.</span>
      </div>
    </Layout.FullWidth>
    <h5 {...{
      "id": "debit-transactions"
    }}>{`Debit transactions`}</h5>
    <p>{`The following balance-based decisions are applicable for debits unless you are using Signal Transaction Scores rules. If you use the default balance-based rules, you will see the following:`}</p>
    <Layout.FullWidth>
      <table>
  <thead>
    <tr>
      <th>Transfer Type</th>
      <th>Scenario</th>
      <th>Authorization Decision</th>
      <th style={{
              minWidth: "210px"
            }}>Decision Rationale Code</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td>Debit</td>
      <td>Item was manually verified through Database Auth and result was{" "}
        <code>database_insights_pass_with_caution</code></td>
      <td><code>approved</code>*</td>
      <td><code>"MANUALLY_VERIFIED_ITEM"</code></td>
    </tr>
    <tr>
      <td>Debit</td>
      <td>Account balance could not be verified due to an error fetching the balance</td>
      <td><code>approved</code>*</td>
      <td><code>"ERROR"</code></td>
    </tr>
    <tr>
      <td>Debit</td>
      <td>Account balance could not be verified because a credentials-based Item was disconnected.</td>
      <td><code>approved</code>*</td>
      <td><code>"ITEM_LOGIN_REQUIRED"</code></td>
    </tr>
    <tr>
      <td>Debit</td>
      <td>User account doesn't have sufficient balance to complete the debit transfer</td>
      <td><code>declined</code></td>
      <td><code>"NSF"</code></td>
    </tr>
    <tr>
      <td>Debit</td>
      <td>There are sufficient funds in the account balance for the transaction amount</td>
      <td><code>approved</code></td>
      <td><code>null</code></td>
    </tr>
  </tbody>
      </table>
      <div style={{
        fontSize: "14px",
        marginBottom: "24px"
      }}> 
        <span> * Default value; can be <a href="#creating-additional-custom-rulesets"><span style={{
              textDecoration: "underline"
            }}>customized</span></a>.</span>
      </div>
    </Layout.FullWidth>
    <p>{`Many of these rules can be `}<a parentName="p" {...{
        "href": "#creating-additional-custom-rulesets"
      }}>{`customized`}</a>{` through the Rules section of the Transfer Dashboard.`}</p>
    <p>{`Note: For debits, if an Item is in `}<inlineCode parentName="p">{`ITEM_LOGIN_REQUIRED`}</inlineCode>{` state, the default ruleset outcome will be `}<inlineCode parentName="p">{`ACCEPT`}</inlineCode>{`. If you wish to change this behavior, you must customize the ruleset.`}</p>
    <h4 {...{
      "id": "testing-signal-rules-in-sandbox"
    }}>{`Testing Signal Rules in Sandbox`}</h4>
    <p>{`The Signal Dashboard lets you switch between Sandbox and Production environments. You can create or edit Signal rulesets in Sandbox mode just as you would in Production. To test rulesets you have created in Sandbox, make sure to pass the appropriate `}<inlineCode parentName="p">{`ruleset_key`}</inlineCode>{` to your `}<inlineCode parentName="p">{`/transfer/authorization/create`}</inlineCode>{` call while using the Sandbox environment.`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`To simulate the account having insufficient balance, you can either use `}<a parentName="p" {...{
            "href": "/docs/sandbox/user-custom/#configuring-the-custom-user-account"
          }}>{`custom Sandbox users`}</a>{` to create a user with a specific balance, or simply attempt a transfer for a higher amount than the available balance in that account. You can verify the available balance by making a call to `}<inlineCode parentName="p">{`/accounts/balance/get`}</inlineCode>{`.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`To simulate the Item going into an `}<inlineCode parentName="p">{`ITEM_LOGIN_REQUIRED`}</inlineCode>{` state, call `}<inlineCode parentName="p">{`/sandbox/item/reset_login`}</inlineCode>{`, passing in that account's access token.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`To simulate an Item that was verified through Database Auth, follow the instructions for `}<a parentName="p" {...{
            "href": "/docs/auth/coverage/testing/#testing-database-auth-or-database-insights"
          }}>{`Testing Database Auth`}</a>{` in Sandbox.`}</p>
      </li>
    </ul>
    <h4 {...{
      "id": "for-reseller-partners"
    }}>{`For Reseller Partners`}</h4>
    <p>{`A reseller partner will have a global Signal ruleset that all their end customers inherit. If the reseller makes a change to that global default ruleset, all the end-customers also automatically adopt that change as well. End customers can also create their own rulesets, which will not propagate back to the reseller partner. `}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "default-ruleset-for-real-time-balance-checks",
        level: 4,
        title: "Default ruleset for real-time balance checks",
      },
{
        id: "creating-additional-custom-rulesets",
        level: 4,
        title: "Creating additional custom rulesets",
      },
{
        id: "using-rulesets-during-transfer-authorization",
        level: 4,
        title: "Using rulesets during transfer authorization",
      },
{
        id: "default-authorization-decisions",
        level: 4,
        title: "Default authorization decisions",
      },
{
        id: "testing-signal-rules-in-sandbox",
        level: 4,
        title: "Testing Signal Rules in Sandbox",
      },
{
        id: "for-reseller-partners",
        level: 4,
        title: "For Reseller Partners",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
